<template lang="pug">
.row
  .col-sm-12.basic-filters
    label(v-if="label") {{ label }}
    AppSelect(
      v-model="selected"
      :disabled="disabled"
      :disabled-tooltip="disabledTooltip"
      :options="groupedOptions"
      :select2-options="select2Options"
      :max-visible-options="maxVisibleOptions"
      :label="label"
    )
</template>

<script>
import { isNil, isArray } from 'lodash-es';
import {
  decorateIdentifier,
  decorateGroupWithUniqueIdentifier,
  parseDecoratedIdentifier,
} from '~/utils/local_id_utils';
import { mapGetters } from 'vuex';

export default {
  name: 'ReportBaseSelectFilter',
  props: {
    // queryProperty can be passed for all options in the queryProperty prop
    // OR separately for each option inside options prop
    // e.g. options: { id: 1, text: 'Option 1', queryProperty: 'query_property_1' }
    // this is especially useful if options have children and options are treated as separate sets
    label: {
      type: String,
    },
    queryProperty: {
      type: String,
      required: false,
    },
    queryFilter: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledTooltip: {
      type: String,
    },
    maxVisibleOptions: {
      type: Number,
      required: false,
      default: 5,
    },
  },
  data() {
    const select2Options = {
      allowClear: this.allowClear,
      multiple: this.multiple,
      placeholder: this.placeholder,
      width: '100%',
    };

    return {
      select2Options,
    };
  },
  computed: {
    ...mapGetters(['reportQuery']),
    queryProperties() {
      return this.queryProperty ? [this.queryProperty] : this.options.map((option) => option.queryProperty);
    },
    groupedOptions() {
      if (isNil(this.options)) {
        return this.multiple ? [] : null;
      }

      return this.multiple
        ? this.options.map(({ id, text, children, queryProperty }) => ({
            id: isNil(id) ? undefined : decorateIdentifier(id, queryProperty || this.queryProperty),
            text,
            children: isArray(children)
              ? decorateGroupWithUniqueIdentifier(children, queryProperty || this.queryProperty)
              : undefined,
          }))
        : this.options;
    },
    selected: {
      get() {
        const filters = this.queryFilter ? this.reportQuery.filters : this.reportQuery;

        if (this.multiple) {
          const currentValues = this.queryProperties.map(
            (property) => !isNil(filters[property]) && filters[property].map((id) => decorateIdentifier(id, property)),
          );

          return currentValues.flat().filter(Boolean) || [];
        }

        return filters[this.queryProperties[0]] || null;
      },
      set(selectedOptions) {
        if (this.multiple) {
          this.queryValues(selectedOptions.map(this.mapLocalIdentifier)).map((value) =>
            this.$store.dispatch('updateSearch', { [value.queryProperty]: value.ids }),
          );
        } else {
          this.$store.dispatch('updateSearch', { [this.queryProperties[0]]: selectedOptions });
        }
      },
    },
  },
  methods: {
    mapLocalIdentifier(localId) {
      const id = parseDecoratedIdentifier(localId);

      return {
        queryProperty: id.decorator,
        id: id.id,
      };
    },
    queryValues(selectedOptions) {
      return this.queryProperties.map((queryProperty) => {
        const ids = selectedOptions
          .filter((option) => option.queryProperty === queryProperty)
          .map((option) => option.id);

        return {
          queryProperty,
          ids: selectedOptions.length === 0 || ids.length > 0 ? ids : [-1],
        };
      });
    },
  },
};
</script>
